import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import type { List } from 'app/generated/graphql'
import type { PipelineType } from 'app/types/entities'
import type { ListView, SystemListDef } from 'app/types/navigation'
import { isCompanionPipeline } from 'lib/companionPipelines'
import { isFilterList, isPracticeList } from 'lib/practiceLists'
import { SYSTEM_LIST_BY_SLUG } from 'lib/systemLists'

export type ListDef =
  | { source: `system`; slug: string; def: SystemListDef }
  | { source: `manual`; slug: string; list: List }
  | { source: `filter`; slug: string; list: List }

export const MANUAL_LIST_VIEWS: readonly ListView[] = [`board`, `table`]
export const FILTER_LIST_VIEWS: readonly ListView[] = [`board`, `table`]

export function isManualListPipeline(pipeline: PipelineType): boolean {
  return !!pipeline.id && !isSystemPipelineId(pipeline.id) && !isCompanionPipeline(pipeline)
}

/**
 * System slug first, then a practice list by id. A system list is never a practice list, and a
 * rule is what makes one a filter list rather than a manual one.
 */
export function resolveList(slug: string, lists: readonly List[]): ListDef | undefined {
  const def = SYSTEM_LIST_BY_SLUG.get(slug)
  if (def) return { source: `system`, slug, def }
  const list = lists.find((candidate) => candidate.id === slug && isPracticeList(candidate))
  if (!list) return undefined
  return { source: isFilterList(list) ? `filter` : `manual`, slug, list }
}

export function listViews(list: ListDef): readonly ListView[] {
  if (list.source === `system`) return list.def.views
  if (list.source === `filter`) return FILTER_LIST_VIEWS
  return MANUAL_LIST_VIEWS
}

export function listName(list: ListDef): string {
  if (list.source === `system`) return list.def.name
  return list.list.name || `Untitled list`
}

export function manualListPath(pipelineId: string, view?: ListView): string {
  return view ? `/lists/${pipelineId}?view=${view}` : `/lists/${pipelineId}`
}

export function filterListPath(customPageId: string, view?: ListView): string {
  return view ? `/lists/${customPageId}?view=${view}` : `/lists/${customPageId}`
}

/**
 * Where somebody goes once the manual list they were looking at is deleted: the next list in
 * their saved Lists order, and the Action Center when that was the last one. The list's page
 * and its sidebar row both ask here, so a delete lands in the same place either way.
 */
export function afterListDeletedPath(
  deletedId: string,
  lists: readonly List[],
  order: readonly string[],
  workspaceQuery: string,
): string {
  const remaining = lists.filter((list) => list.id !== deletedId)
  return `${firstManualListPath(remaining, order) ?? `/action-center`}${workspaceQuery}`
}

/**
 * The manual list `/pipelines` now means: the first one the person put at the top of
 * their Lists, and failing that the first the workspace has. Saved keys are the nav's
 * own (`manual:<id>`), so a key for a list that is gone, or for another kind of list,
 * is simply passed over.
 */
export function firstManualListPath(lists: readonly List[], order: readonly string[]): string | undefined {
  const manual = lists.filter((list) => isPracticeList(list) && !isFilterList(list))
  const saved = order
    .filter((key) => key.startsWith(`manual:`))
    .map((key) => key.slice(`manual:`.length))
    .find((id) => manual.some((list) => list.id === id))
  const id = saved ?? manual[0]?.id
  return id ? manualListPath(id) : undefined
}
